/**
 * Product Service
 * 
 * Fetches products from Firestore Products collection
 */

import { collection, getDocs, query, where, orderBy, limit, startAfter, QueryDocumentSnapshot, DocumentData, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface Product {
  ItemNameEN: string;
  ItemNameFR: string;
  ItemCode: string;
  ItemForm: 'Vegetable' | 'Flower' | 'Heritage' | 'Livingston';
  ItemCategoryEN: string;
  ItemCategoryFR: string;
  ItemSubCategoryEN: string;
  ItemSubCategoryFR: string;
  Price: number;
  status?: 'available' | 'out-of-stock';
}

export interface ProductFilters {
  itemForm?: 'Vegetable' | 'Flower' | 'Heritage' | 'Livingston';
  category?: string;
  subCategory?: string;
  searchTerm?: string;
  minPrice?: number;
  maxPrice?: number;
}

/**
 * Fetch all products from Firestore
 */
export async function getAllProducts(): Promise<Product[]> {
  try {
    const productsRef = collection(db, 'Products');
    const snapshot = await getDocs(query(productsRef, orderBy('ItemCode')));
    
    return snapshot.docs.map(doc => doc.data() as Product);
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
}

/**
 * Fetch products with filters
 */
export async function getProducts(filters?: ProductFilters): Promise<Product[]> {
  try {
    let productsQuery: any = collection(db, 'Products');
    const constraints: any[] = [];

    // Apply filters
    if (filters?.itemForm) {
      constraints.push(where('ItemForm', '==', filters.itemForm));
    }
    
    if (filters?.category) {
      constraints.push(where('ItemCategoryEN', '==', filters.category));
    }
    
    if (filters?.subCategory) {
      constraints.push(where('ItemSubCategoryEN', '==', filters.subCategory));
    }
    
    if (filters?.minPrice !== undefined) {
      constraints.push(where('Price', '>=', filters.minPrice));
    }
    
    if (filters?.maxPrice !== undefined) {
      constraints.push(where('Price', '<=', filters.maxPrice));
    }

    // Add ordering
    constraints.push(orderBy('ItemCode'));

    if (constraints.length > 0) {
      productsQuery = query(productsQuery, ...constraints);
    }

    const snapshot = await getDocs(productsQuery);
    let products = snapshot.docs.map(doc => doc.data() as Product);

    // Apply search filter in memory (Firestore doesn't support full-text search)
    if (filters?.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      products = products.filter(product => 
        product.ItemNameEN.toLowerCase().includes(searchLower) ||
        product.ItemNameFR.toLowerCase().includes(searchLower) ||
        product.ItemCode.toLowerCase().includes(searchLower) ||
        product.ItemCategoryEN.toLowerCase().includes(searchLower) ||
        product.ItemSubCategoryEN.toLowerCase().includes(searchLower)
      );
    }

    return products;
  } catch (error) {
    console.error('Error fetching products with filters:', error);
    throw error;
  }
}

/**
 * Get products by ItemForm
 */
export async function getProductsByForm(itemForm: 'Vegetable' | 'Flower' | 'Heritage' | 'Livingston'): Promise<Product[]> {
  try {
    const productsRef = collection(db, 'Products');
    // First try with orderBy - if it fails due to missing index, fall back to without orderBy
    let snapshot;
    try {
      const q = query(productsRef, where('ItemForm', '==', itemForm), orderBy('ItemCode'));
      snapshot = await getDocs(q);
    } catch (indexError: any) {
      // If it's an index error, try without orderBy
      if (indexError?.code === 'failed-precondition' || indexError?.message?.includes('index')) {
        console.warn('Firestore index not found, fetching without orderBy. Consider creating a composite index for ItemForm and ItemCode.');
        const q = query(productsRef, where('ItemForm', '==', itemForm));
        snapshot = await getDocs(q);
        // Sort in memory instead
        const products = snapshot.docs.map(doc => doc.data() as Product);
        return products.sort((a, b) => a.ItemCode.localeCompare(b.ItemCode));
      }
      throw indexError;
    }
    
    return snapshot.docs.map(doc => doc.data() as Product);
  } catch (error: any) {
    console.error(`Error fetching ${itemForm} products:`, error);
    // Log more details about the error
    if (error?.code) {
      console.error('Firestore error code:', error.code);
    }
    if (error?.message) {
      console.error('Firestore error message:', error.message);
    }
    throw error;
  }
}

/**
 * Get a single product by ItemCode
 */
export async function getProductByCode(itemCode: string): Promise<Product | null> {
  try {
    const productsRef = collection(db, 'Products');
    const snapshot = await getDocs(query(productsRef, where('ItemCode', '==', itemCode), limit(1)));
    
    if (snapshot.empty) {
      return null;
    }
    
    return snapshot.docs[0].data() as Product;
  } catch (error) {
    console.error('Error fetching product by code:', error);
    throw error;
  }
}

/**
 * Get multiple products by ItemCodes (batch fetch)
 * Firestore 'in' queries are limited to 10 items, so this handles batching
 */
export async function getProductsByCodes(itemCodes: string[]): Promise<Map<string, Product>> {
  const productMap = new Map<string, Product>();
  
  if (!itemCodes || itemCodes.length === 0) {
    return productMap;
  }

  try {
    // Remove duplicates and filter out empty values
    const uniqueCodes = Array.from(new Set(itemCodes.filter(code => code && code.trim())));
    
    if (uniqueCodes.length === 0) {
      return productMap;
    }

    const productsRef = collection(db, 'Products');
    const BATCH_SIZE = 10; // Firestore 'in' query limit
    
    // Process in batches of 10
    for (let i = 0; i < uniqueCodes.length; i += BATCH_SIZE) {
      const batch = uniqueCodes.slice(i, i + BATCH_SIZE);
      
      try {
        const q = query(productsRef, where('ItemCode', 'in', batch));
        const snapshot = await getDocs(q);
        
        snapshot.forEach((doc) => {
          const product = doc.data() as Product;
          productMap.set(product.ItemCode, product);
        });
      } catch (error: any) {
        console.error(`Error fetching batch of products (codes: ${batch.join(', ')}):`, error);
        // Continue with next batch even if one fails
      }
    }
    
    console.log(`[ProductService] Fetched ${productMap.size} products from ${uniqueCodes.length} unique ItemCodes`);
    return productMap;
  } catch (error) {
    console.error('Error fetching products by codes:', error);
    throw error;
  }
}

/**
 * Get unique categories for a given ItemForm
 */
export async function getCategoriesByForm(itemForm: 'Vegetable' | 'Flower' | 'Heritage' | 'Livingston'): Promise<string[]> {
  const products = await getProductsByForm(itemForm);
  const categories = new Set(products.map(p => p.ItemCategoryEN));
  return Array.from(categories).sort();
}

/**
 * Get unique subcategories for a given ItemForm and category
 */
export async function getSubCategoriesByFormAndCategory(
  itemForm: 'Vegetable' | 'Flower' | 'Heritage' | 'Livingston',
  category: string
): Promise<string[]> {
  const products = await getProducts({ itemForm, category });
  const subCategories = new Set(products.map(p => p.ItemSubCategoryEN));
  return Array.from(subCategories).sort();
}

/**
 * Convert Product to form-compatible format
 */
export function productToFormItem(product: Product, quantity: number = 0) {
  return {
    id: product.ItemCode,
    packetDescription: product.ItemNameEN,
    code: product.ItemCode,
    quantity,
    price: product.Price,
    category: product.ItemForm.toLowerCase() as 'vegetable' | 'flower' | 'heritage' | 'livingston',
    // Additional fields for compatibility
    itemNameEN: product.ItemNameEN,
    itemNameFR: product.ItemNameFR,
    itemCategoryEN: product.ItemCategoryEN,
    itemCategoryFR: product.ItemCategoryFR,
    itemSubCategoryEN: product.ItemSubCategoryEN,
    itemSubCategoryFR: product.ItemSubCategoryFR,
  };
}

/**
 * Update a product's availability status
 */
export async function updateProductStatus(
  itemCode: string,
  status: 'available' | 'out-of-stock'
): Promise<void> {
  try {
    const productRef = doc(db, 'Products', itemCode);
    await updateDoc(productRef, { status });
  } catch (error) {
    console.error(`Error updating product status for ${itemCode}:`, error);
    throw error;
  }
}

/**
 * Fetch all products with their status (defaults to 'available' if not set)
 */
export async function getAllProductsWithStatus(): Promise<Product[]> {
  try {
    const productsRef = collection(db, 'Products');
    const snapshot = await getDocs(query(productsRef, orderBy('ItemForm'), orderBy('ItemCode')));
    return snapshot.docs.map(docSnap => ({
      ...(docSnap.data() as Product),
      status: (docSnap.data().status as 'available' | 'out-of-stock') || 'available',
    }));
  } catch (error: any) {
    if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
      const productsRef = collection(db, 'Products');
      const snapshot = await getDocs(productsRef);
      const products = snapshot.docs.map(docSnap => ({
        ...(docSnap.data() as Product),
        status: (docSnap.data().status as 'available' | 'out-of-stock') || 'available',
      }));
      return products.sort((a, b) =>
        a.ItemForm.localeCompare(b.ItemForm) || a.ItemCode.localeCompare(b.ItemCode)
      );
    }
    console.error('Error fetching products with status:', error);
    throw error;
  }
}

